import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, Users, Package, Images, LogOut, Loader2, Plus, Trash2, X, UploadCloud, ExternalLink } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BUSINESS } from "../config";

const STATUS_COLORS = { new: "text-[#FF4A17]", quoted: "text-amber-400", won: "text-emerald-400", lost: "text-[var(--text-secondary)]" };

function Stat({ label, value, testid }) {
  return (
    <div className="bg-[#0a0a0a] border border-[var(--border-dim)] p-6" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--text-secondary)]">{label}</p>
      <p className="font-display text-4xl font-black mt-2">{value}</p>
    </div>
  );
}

function ImageUploader({ images, setImages, testid }) {
  const [busy, setBusy] = useState(false);
  const onDrop = useCallback(async (accepted) => {
    setBusy(true);
    try {
      const out = [];
      for (const f of accepted) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        out.push(data.path);
      }
      setImages([...images, ...out]);
    } catch { toast.error("Upload failed"); } finally { setBusy(false); }
  }, [images, setImages]);
  const { getRootProps, getInputProps } = useDropzone({ onDrop, accept: { "image/*": [] } });
  return (
    <div>
      <div {...getRootProps()} data-testid={testid} className="border border-dashed border-[var(--border-dim)] hover:border-white p-4 text-center cursor-pointer transition-colors">
        <input {...getInputProps()} />
        {busy ? <Loader2 className="mx-auto animate-spin text-[var(--accent)]" size={18} /> : <UploadCloud className="mx-auto text-[var(--text-secondary)]" size={18} />}
        <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--text-secondary)] mt-2">Upload images</p>
      </div>
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {images.map((im, i) => (
            <div key={i} className="relative h-16 w-16 border border-[var(--border-dim)]">
              <img src={im.startsWith("http") ? im : fileUrl(im)} alt="" className="w-full h-full object-cover" />
              <button onClick={() => setImages(images.filter((_, idx) => idx !== i))} className="absolute -top-2 -right-2 bg-black border border-[var(--border-dim)] p-0.5"><X size={12} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const EMPTY_LISTING = { title: "", category: "goods", condition: "used", price: "", description: "", images: [], status: "available" };

export default function Admin() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [listings, setListings] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [editing, setEditing] = useState(null); // listing form
  const [galForm, setGalForm] = useState({ image: [], caption: "" });

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  const loadAll = useCallback(async () => {
    try {
      const [s, l, li, g] = await Promise.all([
        api.get("/admin/stats"), api.get("/leads"), api.get("/listings", { params: { include_hidden: true } }), api.get("/gallery"),
      ]);
      setStats(s.data); setLeads(l.data); setListings(li.data); setGallery(g.data);
    } catch {}
  }, []);

  useEffect(() => { if (user) loadAll(); }, [user, loadAll]);

  const doLogout = async () => { await logout(); navigate("/"); };

  const updateLead = async (id, patch) => {
    await api.patch(`/leads/${id}`, patch);
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };
  const deleteLead = async (id) => { await api.delete(`/leads/${id}`); setLeads((p) => p.filter((l) => l.id !== id)); toast.success("Lead removed"); };

  const saveListing = async () => {
    if (!editing.title || !editing.price) { toast.error("Title and price required"); return; }
    const payload = { ...editing, price: parseFloat(editing.price) };
    try {
      if (editing.id) { const { data } = await api.put(`/listings/${editing.id}`, payload); setListings((p) => p.map((x) => (x.id === data.id ? data : x))); }
      else { const { data } = await api.post("/listings", payload); setListings((p) => [data, ...p]); }
      toast.success("Listing saved"); setEditing(null); loadAll();
    } catch { toast.error("Save failed"); }
  };
  const deleteListing = async (id) => { await api.delete(`/listings/${id}`); setListings((p) => p.filter((x) => x.id !== id)); toast.success("Listing deleted"); };

  const addGallery = async () => {
    if (!galForm.image.length) { toast.error("Add an image"); return; }
    const { data } = await api.post("/gallery", { image: galForm.image[0], caption: galForm.caption });
    setGallery((p) => [data, ...p]); setGalForm({ image: [], caption: "" }); toast.success("Added to gallery");
  };
  const deleteGallery = async (id) => { await api.delete(`/gallery/${id}`); setGallery((p) => p.filter((x) => x.id !== id)); };

  if (loading || !user) return <div className="bg-[#050505] min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[var(--accent)]" /></div>;

  const TABS = [
    { v: "overview", l: "Overview", icon: LayoutDashboard },
    { v: "leads", l: "CRM / Leads", icon: Users },
    { v: "listings", l: "Listings", icon: Package },
    { v: "gallery", l: "Gallery", icon: Images },
  ];

  return (
    <div className="bg-[#050505] min-h-screen flex flex-col lg:flex-row">
      {/* sidebar */}
      <aside className="lg:w-64 border-b lg:border-b-0 lg:border-r border-[var(--border-dim)] flex lg:flex-col lg:min-h-screen">
        <div className="p-6 lg:p-8 flex-1">
          <p className="font-display font-black uppercase text-xl leading-none hidden lg:block">Austin<span className="text-[var(--accent)]">/</span>Abatement</p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--text-secondary)] mt-2 hidden lg:block">Admin Console</p>
          <nav className="flex lg:flex-col gap-1 lg:mt-10">
            {TABS.map((t) => (
              <button key={t.v} data-testid={`tab-${t.v}`} onClick={() => setTab(t.v)} className={`flex items-center gap-3 px-4 py-3 font-mono text-xs uppercase tracking-widest transition-colors ${tab === t.v ? "bg-[var(--accent)] text-white" : "text-[var(--text-secondary)] hover:text-white"}`}>
                <t.icon size={15} /> <span className="hidden sm:inline">{t.l}</span>
              </button>
            ))}
          </nav>
        </div>
        <div className="p-6 lg:p-8 flex items-center gap-3">
          <button onClick={doLogout} data-testid="logout-btn" className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--accent)] transition-colors"><LogOut size={14} /> Sign out</button>
        </div>
      </aside>

      {/* main */}
      <main className="flex-1 p-5 md:p-10 max-w-full overflow-x-hidden">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tighter">{TABS.find((t) => t.v === tab).l}</h1>
          <span className="font-mono text-xs text-[var(--text-secondary)] hidden md:block">{user.email}</span>
        </div>

        {tab === "overview" && stats && (
          <div className="space-y-8" data-testid="overview-panel">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <Stat label="Total Leads" value={stats.leads_total} testid="stat-leads" />
              <Stat label="New Leads" value={stats.leads_new} testid="stat-new" />
              <Stat label="Live Listings" value={stats.listings_available} testid="stat-live" />
              <Stat label="Total Listings" value={stats.listings_total} testid="stat-listings" />
              <Stat label="Items Sold" value={stats.sold} testid="stat-sold" />
              <Stat label="Revenue" value={`$${stats.revenue.toLocaleString()}`} testid="stat-revenue" />
            </div>
            <div className="border border-[var(--border-dim)] bg-[#0a0a0a] p-6">
              <p className="font-mono text-xs uppercase tracking-widest text-[var(--text-secondary)] mb-4">Recent leads</p>
              {leads.slice(0, 5).map((l) => (
                <div key={l.id} className="flex items-center justify-between py-3 border-b border-[var(--border-dim)] last:border-0">
                  <span className="font-display font-bold uppercase">{l.name}</span>
                  <span className={`font-mono text-xs uppercase ${STATUS_COLORS[l.status]}`}>{l.status}</span>
                </div>
              ))}
              {leads.length === 0 && <p className="font-mono text-sm text-[var(--text-secondary)]">No leads yet.</p>}
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div className="space-y-4" data-testid="leads-panel">
            {leads.length === 0 && <p className="font-mono text-sm text-[var(--text-secondary)]">No bid requests yet.</p>}
            {leads.map((l) => (
              <div key={l.id} className="border border-[var(--border-dim)] bg-[#0a0a0a] p-5 md:p-6" data-testid={`lead-${l.id}`}>
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display text-2xl font-bold uppercase">{l.name}</h3>
                      {l.out_of_town && <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--accent)] border border-[var(--accent)] px-2 py-0.5">Out of town</span>}
                      <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--text-secondary)] border border-[var(--border-dim)] px-2 py-0.5">via {l.channel}</span>
                    </div>
                    <div className="flex flex-wrap gap-4 mt-2 font-mono text-xs text-[var(--text-secondary)]">
                      {l.phone && <a href={`tel:${l.phone}`} className="hover:text-white">{l.phone}</a>}
                      {l.email && <a href={`mailto:${l.email}`} className="hover:text-white">{l.email}</a>}
                      <span>{new Date(l.created_at).toLocaleDateString()}</span>
                    </div>
                    {l.address && <p className="text-sm text-[var(--text-secondary)] mt-2">📍 {l.address}</p>}
                    {l.description && <p className="text-sm mt-2">{l.description}</p>}
                    {l.file_paths?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {l.file_paths.map((p, i) => (
                          <a key={i} href={fileUrl(p)} target="_blank" rel="noreferrer" className="h-16 w-16 border border-[var(--border-dim)] overflow-hidden block hover:border-[var(--accent)]">
                            {/\.(png|jpe?g|webp|gif|heic)$/i.test(p) ? <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" /> : <span className="flex items-center justify-center h-full"><ExternalLink size={16} /></span>}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 md:w-52">
                    <select data-testid={`lead-status-${l.id}`} value={l.status} onChange={(e) => updateLead(l.id, { status: e.target.value })} className="bg-[#050505] border border-[var(--border-dim)] px-3 py-2 font-mono text-xs uppercase focus:border-[var(--accent)] focus:outline-none">
                      {["new", "quoted", "won", "lost"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <input data-testid={`lead-quote-${l.id}`} type="number" placeholder="Quote $" defaultValue={l.quote_amount || ""} onBlur={(e) => updateLead(l.id, { quote_amount: e.target.value ? parseFloat(e.target.value) : null })} className="bg-[#050505] border border-[var(--border-dim)] px-3 py-2 font-mono text-xs focus:border-[var(--accent)] focus:outline-none" />
                    <textarea data-testid={`lead-notes-${l.id}`} placeholder="Notes" defaultValue={l.notes} onBlur={(e) => updateLead(l.id, { notes: e.target.value })} rows={2} className="bg-[#050505] border border-[var(--border-dim)] px-3 py-2 font-mono text-xs focus:border-[var(--accent)] focus:outline-none resize-none" />
                    <button onClick={() => deleteLead(l.id)} className="flex items-center justify-center gap-1 text-[var(--text-secondary)] hover:text-[var(--accent)] font-mono text-[10px] uppercase tracking-widest py-1"><Trash2 size={12} /> Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "listings" && (
          <div data-testid="listings-panel">
            <button onClick={() => setEditing({ ...EMPTY_LISTING })} data-testid="new-listing-btn" className="mb-6 inline-flex items-center gap-2 btn-wipe bg-[var(--accent)] text-white hover:text-white px-6 py-3 font-mono text-xs uppercase tracking-widest font-bold"><Plus size={14} /> New listing</button>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {listings.map((l) => (
                <div key={l.id} className="border border-[var(--border-dim)] bg-[#0a0a0a]" data-testid={`admin-listing-${l.id}`}>
                  <div className="aspect-[4/3] bg-[#050505] overflow-hidden">
                    {l.images?.[0] ? <img src={l.images[0].startsWith("http") ? l.images[0] : fileUrl(l.images[0])} alt="" className="w-full h-full object-cover" /> : <div className="flex items-center justify-center h-full font-mono text-[10px] text-[var(--text-secondary)]">NO IMAGE</div>}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-display font-bold uppercase">{l.title}</h3>
                      <span className="font-mono text-sm">${Number(l.price).toLocaleString()}</span>
                    </div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--text-secondary)] mt-1">{l.category} · {l.condition} · <span className={l.status === "sold" ? "text-[var(--accent)]" : l.status === "hidden" ? "text-amber-400" : "text-emerald-400"}>{l.status}</span></p>
                    <div className="flex gap-2 mt-4">
                      <button onClick={() => setEditing({ ...l, price: String(l.price) })} data-testid={`edit-listing-${l.id}`} className="flex-1 border border-[var(--border-dim)] hover:border-white py-2 font-mono text-[10px] uppercase tracking-widest transition-colors">Edit</button>
                      <button onClick={() => deleteListing(l.id)} className="border border-[var(--border-dim)] hover:border-[var(--accent)] hover:text-[var(--accent)] px-3 py-2 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "gallery" && (
          <div data-testid="gallery-panel">
            <div className="border border-[var(--border-dim)] bg-[#0a0a0a] p-5 mb-6 max-w-lg space-y-3">
              <ImageUploader images={galForm.image} setImages={(imgs) => setGalForm({ ...galForm, image: imgs.slice(-1) })} testid="gallery-uploader" />
              <input value={galForm.caption} onChange={(e) => setGalForm({ ...galForm, caption: e.target.value })} placeholder="Caption (optional)" className="w-full bg-[#050505] border border-[var(--border-dim)] px-3 py-2 font-mono text-xs focus:border-[var(--accent)] focus:outline-none" />
              <button onClick={addGallery} data-testid="add-gallery-btn" className="btn-wipe bg-[var(--accent)] text-white hover:text-white px-6 py-2.5 font-mono text-xs uppercase tracking-widest font-bold">Add to gallery</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {gallery.map((g) => (
                <div key={g.id} className="relative group aspect-square border border-[var(--border-dim)] overflow-hidden">
                  <img src={g.image.startsWith("http") ? g.image : fileUrl(g.image)} alt={g.caption} className="w-full h-full object-cover" />
                  <button onClick={() => deleteGallery(g.id)} className="absolute top-2 right-2 bg-black/70 p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
                </div>
              ))}
              {gallery.length === 0 && <p className="font-mono text-sm text-[var(--text-secondary)] col-span-full">No gallery images yet — the site shows defaults until you add your own.</p>}
            </div>
          </div>
        )}
      </main>

      {/* listing editor modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="listing-modal">
          <div className="bg-[#0a0a0a] border border-[var(--border-dim)] w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-black uppercase">{editing.id ? "Edit" : "New"} listing</h2>
              <button onClick={() => setEditing(null)}><X /></button>
            </div>
            <div className="space-y-4">
              <input data-testid="listing-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Title *" className="w-full bg-[#050505] border border-[var(--border-dim)] px-4 py-3 font-mono text-sm focus:border-[var(--accent)] focus:outline-none" />
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="listing-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="bg-[#050505] border border-[var(--border-dim)] px-4 py-3 font-mono text-xs uppercase focus:border-[var(--accent)] focus:outline-none">
                  {["goods", "jewelry", "vehicles"].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select data-testid="listing-condition" value={editing.condition} onChange={(e) => setEditing({ ...editing, condition: e.target.value })} className="bg-[#050505] border border-[var(--border-dim)] px-4 py-3 font-mono text-xs uppercase focus:border-[var(--accent)] focus:outline-none">
                  {["new", "used"].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="listing-price" type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} placeholder="Price (USD) *" className="bg-[#050505] border border-[var(--border-dim)] px-4 py-3 font-mono text-sm focus:border-[var(--accent)] focus:outline-none" />
                <select data-testid="listing-status" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="bg-[#050505] border border-[var(--border-dim)] px-4 py-3 font-mono text-xs uppercase focus:border-[var(--accent)] focus:outline-none">
                  {["available", "sold", "hidden"].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <textarea data-testid="listing-description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Description" rows={4} className="w-full bg-[#050505] border border-[var(--border-dim)] px-4 py-3 font-mono text-sm focus:border-[var(--accent)] focus:outline-none resize-none" />
              <ImageUploader images={editing.images} setImages={(imgs) => setEditing({ ...editing, images: imgs })} testid="listing-uploader" />
              <button onClick={saveListing} data-testid="save-listing-btn" className="w-full btn-wipe bg-[var(--accent)] text-white hover:text-white py-3.5 font-mono text-sm uppercase tracking-widest font-bold">Save listing</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
